'use client';

import { useStore } from '@/lib/store';
import { cn } from '@/lib/cn';

// We treat the first ~5 reconnect attempts as a transient gap (deploy
// window, network blip). Caddy's lb_try_duration=5s already covers the
// usual restart, so anything beyond this is a real outage.
const RECONNECTING_THRESHOLD_ATTEMPTS = 4;

export function MobileHeader(): JSX.Element {
  const connected = useStore((s) => s.connected);
  const attempts = useStore((s) => s.reconnectAttempts);
  const reconnecting = !connected && attempts > 0 && attempts <= RECONNECTING_THRESHOLD_ATTEMPTS;
  const offline = !connected && attempts > RECONNECTING_THRESHOLD_ATTEMPTS;

  const tone = connected ? 'text-ok' : reconnecting ? 'text-warn' : offline ? 'text-err' : 'text-muted';
  const dotTone = connected
    ? 'bg-ok animate-pulse'
    : reconnecting
      ? 'bg-warn animate-pulse'
      : offline
        ? 'bg-err'
        : 'bg-muted';
  const label = connected ? 'live' : reconnecting ? 'reconnecting…' : offline ? 'offline' : 'connecting…';
  const title = connected
    ? 'Live data from executor'
    : reconnecting
      ? 'Reconnecting to executor (likely a deploy restart)'
      : offline
        ? 'Executor unreachable — check status'
        : 'Connecting to executor';

  return (
    <header className="sticky top-0 z-10 border-b border-border bg-bg/95 backdrop-blur">
      <div className="mx-auto max-w-5xl px-4 py-3 flex items-center gap-3">
        <a href="/games" className="font-bold text-accent-strong text-base">LadderBook</a>
        <nav className="hidden sm:flex gap-3 text-muted text-xs">
          <a href="/games" className="hover:text-text">Sports</a>
          <a href="/positions" className="hover:text-text">Positions</a>
          <a href="/profile" className="hover:text-text">Profile</a>
        </nav>
        <span
          className={cn(
            'ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wide',
            tone,
          )}
          title={title}
        >
          <span className={cn('h-2 w-2 rounded-full', dotTone)} />
          {label}
        </span>
      </div>
    </header>
  );
}
